import { ResponseCommand } from './ai-prompt.types';

/**
 * Parses triple-nested toolResult JSON string.
 * This handles the format returned by the new LLM server's tool_complete wrapper.
 *
 * Format: "{'type': 'text', 'text': '{\"commands\":[...]}'}"
 *
 * Parsing steps:
 * 1. Parse outer object: "{...}" → {type: 'text', text: '...'}
 * 2. Extract text field: {type: 'text', text: '...'} → '...'
 * 3. Parse inner JSON: '{\"commands\":[...]}' → {commands: [...]}
 *
 * Handles both proper JSON and Python-style single-quote syntax.
 * Returns null if the inner text is not valid JSON or doesn't contain commands.
 *
 * @param toolResult - The triple-nested JSON string from toolResult command
 * @returns Parsed inner content object with commands, or null if not found/invalid
 */
export function parseToolResult(toolResult: string): { commands?: ResponseCommand[] } | null {
    let result = null;
    try {
        const parsed = JSON.parse(toolResult);
        const innerText = parsed?.text;
        if (typeof innerText === 'string' && innerText.includes('"commands"')) {
            try {
                const innerContent = JSON.parse(innerText);
                result = innerContent != null && typeof innerContent === 'object' ? innerContent : null;
            } catch {
                console.error('Cannot parse text property');
            }
        }
    } catch {
        console.error('Cannot parse tool_result');
    }
    return result;
}

/**
 * Validates if an object is a valid ResponseCommand by checking for required properties.
 *
 * @param obj - Object to validate
 * @returns True if the object has a valid command structure
 */
function isValidResponseCommand(obj: unknown): obj is ResponseCommand {
    if (obj == null || typeof obj !== 'object') return false;
    const command = obj as Record<string, unknown>;
    return typeof command['type'] === 'string' && command['type'].length > 0;
}

/**
 * Extracts commands array from parsed toolResult.
 * Convenience function for the common use case of extracting commands.
 * Filters out invalid commands to ensure robustness.
 *
 * @param toolResult - The triple-nested JSON string from toolResult command
 * @returns Array of valid commands (may be empty), or null on parse error or missing commands array
 */
export function extractCommandsFromToolResult(toolResult: string): ResponseCommand[] | null {
    const innerContent = parseToolResult(toolResult);
    const commands = innerContent?.commands;
    // Filter out invalid commands to prevent runtime errors downstream
    return Array.isArray(commands) ? commands.filter(isValidResponseCommand) : null;
}
